import { useTranslator } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { useInjection } from 'inversify-react';
import { checkAll, checkUserPlan, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { Plan } from '../user/user.store';
import { IWorkspacesStore, WorkspaceRole } from './workspaces.store';

export interface IWorkspacesPermissions {
  canAccessTeam: (workspaceId: string) => boolean;
  canAccessDatasets: (workspaceId: string) => boolean;
  canAccessAttachments: (workspaceId: string) => boolean;
}

interface INewWorkspacesPermissions {
  canDelete: () => IPermissionCheckResult;
  canReset: () => IPermissionCheckResult;
  canCreateWorkspace: () => IPermissionCheckResult;
  canChangeOwner: () => IPermissionCheckResult;
  canRenameWorkspace: () => IPermissionCheckResult;
}

export namespace IWorkspacesPermissions {
  export const $: interfaces.ServiceIdentifier<IWorkspacesPermissions> = Symbol('WORKSPACES_PERMISSIONS');
}

@injectable()
export class WorkspacesPermissions implements IWorkspacesPermissions {
  constructor(@inject(IWorkspacesStore.$) private readonly workspaceStore: IWorkspacesStore) {}

  getWorkspaceRole = (workspaceId: string): WorkspaceRole => {
    return this.workspaceStore.workspaces.find((w) => w.id === workspaceId)?.role ?? WorkspaceRole.Worker;
  };

  canAccessTeam = (workspaceId: string): boolean => {
    const workspaceRole = this.getWorkspaceRole(workspaceId);
    return [WorkspaceRole.Owner, WorkspaceRole.Manager].includes(workspaceRole);
  };

  canAccessDatasets = (workspaceId: string): boolean => {
    const workspaceRole = this.getWorkspaceRole(workspaceId);
    return workspaceRole !== WorkspaceRole.Worker;
  };

  canAccessAttachments = (workspaceId: string): boolean => {
    const workspaceRole = this.getWorkspaceRole(workspaceId);
    return [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer].includes(workspaceRole);
  };
}

export const useWorkspacePermissions = (): INewWorkspacesPermissions => {
  const workspaceStore = useInjection(IWorkspacesStore.$);
  const currentWorkspace = workspaceStore.workspaces.find((w) => w.role === WorkspaceRole.Owner);
  const { t } = useTranslator();

  if (currentWorkspace === undefined) {
    throw new Error('No workspace with Owner role found');
  }

  const canCreateWorkspace = (): IPermissionCheckResult => {
    return checkUserPlan([Plan.Admin, Plan.Training, Plan.Annotation], currentWorkspace, t);
  };

  const canChangeOwner = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Training, Plan.Annotation], [WorkspaceRole.Owner], currentWorkspace, t);
  };

  const canDelete = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Training, Plan.Annotation], [WorkspaceRole.Owner], currentWorkspace, t);
  };

  const canRenameWorkspace = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free], [WorkspaceRole.Owner], currentWorkspace, t);
  };

  const canReset = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free, Plan.Inactive], [WorkspaceRole.Owner], currentWorkspace, t);
  };

  return {
    canCreateWorkspace,
    canChangeOwner,
    canDelete,
    canReset,
    canRenameWorkspace,
  };
};
